import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { TtsFlowComposeWizard } from '../components/TtsFlowComposeWizard';
import { apiErrorMessage, useCreateTtsFlow } from '../api';
import { buildTtsFlowRequest } from '../payload';
import { synthesizePendingTextRows } from '../synthesize';
import {
  DEFAULT_TTS_FORM_VALUES,
  type TtsFlowFormValues,
} from '../types';
import { commitDraftPrompts } from '../lib/draft-prompts';

export function AddTtsFlowPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const create = useCreateTtsFlow();
  const [synthesizing, setSynthesizing] = useState(false);

  const handleSubmit = async (values: TtsFlowFormValues) => {
    setSynthesizing(true);
    try {
      // Auto-synthesize inline text rows into real prompts before submit.
      const rows = await synthesizePendingTextRows({
        userId: Number(userId),
        values,
      });
      const finalValues = { ...values, ttsRows: rows };
      create.mutate(
        buildTtsFlowRequest({ userId: Number(userId), values: finalValues }),
        {
          onSuccess: (res) => {
            if (res?.message === 'Parameters Incorrect') {
              toast.error(res.message);
              return;
            }
            commitDraftPrompts(finalValues.ttsRows);
            toast.success(res?.message ?? 'TTS flow created');
            navigate('/tts-flow-list');
          },
          onError: (e) => toast.error(apiErrorMessage(e, 'Could not create the TTS flow.')),
        },
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Could not synthesize inline text rows.',
      );
    } finally {
      setSynthesizing(false);
    }
  };

  return (
    <TtsFlowComposeWizard
      mode="create"
      initialValues={DEFAULT_TTS_FORM_VALUES}
      isPending={create.isPending || synthesizing}
      onSubmit={handleSubmit}
    />
  );
}
